import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as passengerSlice from '../../../data/passenger.slice';
import { Passenger } from '../../../data/models/passenger.model';
import TextInput from '../../forms/TextInput';
import AddressInput from '../../forms/AddressInput';

const PREFIX = 'PassengerNamedLocationsDialog';

const classes = {
  content: `${PREFIX}-content`,
  message: `${PREFIX}-message`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    width: 500,
    gap: theme.spacing(2),
  },

  [`& .${classes.message}`]: {
    marginBottom: theme.spacing(2),
  },
}));

interface PassengerNamedLocationsDialogProps {
  passenger: Passenger | null;
  open: boolean;
  close: () => void;
}

export default function PassengerNamedLocationsDialog({
  passenger,
  open,
  close,
}: PassengerNamedLocationsDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [error, setError] = React.useState('');
  const requiredFieldMessage = t('common.requiredField');
  const passengerUUID = passenger?.passengerUUID || '';

  const formHook = useForm({
    defaultValues: {
      passengerAddressUseBC: 'RESERVATION',
    },
  });

  const { watch, setValue, control } = formHook;

  const handleClose = () => {
    formHook.reset();
    close();
  };
  const handleSave = (data: any) => {
    if (data.addressStruct) {
      const addressStruct = JSON.parse(data.addressStruct);
      const { addressLine1, city, stateCode } = addressStruct;

      const isExist = passenger?.passengerAddresses?.some(
        (address) =>
          address.addressLine1 === addressLine1 &&
          address.city === city &&
          address.stateCode === stateCode,
      );

      if (!isExist) {
        const { passengerAddressUseBC, label } = data;
        dispatch(
          passengerSlice.addPassengerReservationAddressAsync(
            passengerUUID,
            addressStruct,
            passengerAddressUseBC,
            label,
          ),
        );
        formHook.reset();
        setError('');
        close();
      } else {
        setError(t('snackbar.addresssAlreadyExist'));
      }
    }
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>{t('passenger.addNamedLocation')}</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <AddressInput
            name="address"
            label={t('passenger.namedLocations')}
            required={true}
            watch={watch}
            setValue={setValue}
            errorMessage={error}
          />
          <TextInput
            name="label"
            label={t('passenger.label')}
            control={control}
            required={true}
            rules={{ required: requiredFieldMessage }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid}
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
